import { useLocation } from 'react-router-dom';
import { useEffect, useMemo, useState } from 'react';
import MessagingFlow from './MessagingFlow';
import { useUserStore } from '../../store/userStore';
import { useMessageHubStore } from '../../store/messageHubStore';
import { MessageHubShell } from './MessageHubShell';
import { MESSAGE_HUB_PAGE_SIZE } from './MessageHubListPagination';
import {
  MessageHubListColumn,
  MessageHubListItem,
  HubAvatar,
  formatHubTime,
} from './MessageHubShared';
import { formatQuestionInboxPreview } from '../../utils/questionMessage';

interface Participant {
  id: number;
  name: string;
  company?: string;
  profileImg?: string | null;
}

interface Message {
  id: string;
  text: string;
  sender: Participant;
  createdAt: string;
}

interface Conversation {
  id: string;
  uuid?: string;
  online?: boolean;
  title?: string | null;
  type: string;
  participants: Participant[];
  lastMessage?: Message | null;
  updatedAt: string;
  status: 'active' | 'blocked' | 'muted' | 'removed';
}

export default function MessagesHub() {
  const location = useLocation();
  const [selectedChat, setSelectedChat] = useState<Conversation | null>(null);
  const [search, setSearch] = useState('');
  const [listPage, setListPage] = useState(1);
  const [mobileView, setMobileView] = useState<'list' | 'detail'>('list');
  const user = useUserStore((state) => state.user);
  const conversations = useMessageHubStore((state) => state.conversations) as Conversation[];
  const loading = useMessageHubStore((state) => state.conversationsLoading);
  const fetchConversations = useMessageHubStore((state) => state.fetchConversations);

  const conversationIdParam =
    new URLSearchParams(location.search).get('conversationUuid') ||
    new URLSearchParams(location.search).get('conversationId');

  useEffect(() => {
    const fetchMessages = async () => {
      try {
        await fetchConversations();
      } catch (error) {
        console.error('Error fetching messages:', error);
      }
    };
    void fetchMessages();
  }, [fetchConversations, user?.id]);

  useEffect(() => {
    setListPage(1);
  }, [search]);

  useEffect(() => {
    if (!conversationIdParam || conversations.length === 0) return;

    const matchingConversation = conversations.find(
      (conversation) =>
        String(conversation.id) === String(conversationIdParam) ||
        String(conversation.uuid || '') === String(conversationIdParam)
    );

    if (matchingConversation) {
      setSelectedChat(matchingConversation);
      setMobileView('detail');
    }
  }, [conversationIdParam, conversations]);

  const getOtherParticipant = (chat: Conversation) => {
    const participants = Array.isArray(chat.participants) ? chat.participants : [];
    return participants.find((p) => Number(p.id) !== Number(user?.id)) || participants[0];
  };

  const filteredConversations = conversations.filter((chat) => {
    if (!search.trim()) return true;
    const other = getOtherParticipant(chat);
    const name = String(other?.name || '').toLowerCase();
    const lastMsg = String(chat.lastMessage?.text || '').toLowerCase();
    const term = search.toLowerCase();
    return name.includes(term) || lastMsg.includes(term);
  });

  const totalPages = Math.max(1, Math.ceil(filteredConversations.length / MESSAGE_HUB_PAGE_SIZE));
  const safeListPage = Math.min(listPage, totalPages);
  const pagedConversations = useMemo(
    () =>
      filteredConversations.slice(
        (safeListPage - 1) * MESSAGE_HUB_PAGE_SIZE,
        safeListPage * MESSAGE_HUB_PAGE_SIZE
      ),
    [filteredConversations, safeListPage]
  );

  const listColumn = (
    <MessageHubListColumn
      title="Inbox"
      searchPlaceholder="Search conversations…"
      search={search}
      onSearchChange={setSearch}
      loading={loading}
      loadingLabel="Message Hub"
      emptyMessage="No conversations found."
      listPage={safeListPage}
      totalItems={filteredConversations.length}
      onPageChange={setListPage}
    >
      {pagedConversations.map((chat) => {
        const other = getOtherParticipant(chat);
        const name = other?.name || 'Unknown';
        const lastMsg =
          formatQuestionInboxPreview(chat.lastMessage?.text || '') || 'Start a conversation';
        const timeIso = chat.lastMessage?.createdAt || chat.updatedAt;

        return (
          <MessageHubListItem
            key={chat.id}
            active={selectedChat?.id === chat.id}
            onClick={() => {
              setSelectedChat(chat);
              setMobileView('detail');
            }}
            avatar={
              <HubAvatar
                name={name}
                online={Boolean(chat.online)}
                imageUrl={other?.profileImg || null}
              />
            }
            name={name}
            meta={other?.company}
            preview={lastMsg}
            time={formatHubTime(timeIso)}
          />
        );
      })}
    </MessageHubListColumn>
  );

  const detailColumn = (
    <MessagingFlow
      conversation={selectedChat}
      onBackToInbox={() => {
        setSelectedChat(null);
        setMobileView('list');
      }}
      onConversationDeleted={() => {
        if (!selectedChat) return;
        setSelectedChat(null);
        setMobileView('list');
        void fetchConversations({ force: true });
      }}
    />
  );

  return (
    <MessageHubShell
      active="messages"
      mobileView={mobileView}
      layoutRevision={selectedChat?.id ?? 'empty'}
      listColumn={listColumn}
      detailColumn={detailColumn}
    />
  );
}
